import { CartT, LineItemType } from '@lambda/apis/src/cart/types';
import { GetServerSideProps } from 'next';
import CartApi from '@lambda/apis/src/cart';
import dynamic from 'next/dynamic';
import settings from '@/settings';
import { logger } from '@/lib/logger';
import { CART_COOKIE_NAME } from '@/utils/hooks/cart/constants';
import { ToBeRemovedT } from '@/utils/hooks/cart/types';

// Fills the screen while the chunk loads on a client-side navigation, so the
// footer doesn't jump into view and back (RT-17488).
const CartPageLayout = dynamic(
  () => import('@/components/v2/cart/CartPageLayout'),
  {
    loading: () => <div className="min-h-screen" />,
  },
);

type CartPageProps = {
  cartLinesToBeUpdated: ToBeRemovedT[];
  cart: CartT | null;
};

export const getServerSideProps: GetServerSideProps<
  CartPageProps,
  any
> = async ({ req }) => {
  const cartApi = new CartApi(settings.store);
  let cart: CartT | null = null;
  const cartLinesToBeUpdated: ToBeRemovedT[] = [];

  try {
    const cartId = req?.cookies?.[CART_COOKIE_NAME] as string;

    // Early return if no cartId in Cookies
    if (!cartId) {
      return { props: { cartLinesToBeUpdated, cart } };
    }

    cart = await cartApi.fetchCartById(cartId);

    // Early return if no cart
    if (!cart) {
      return { props: { cartLinesToBeUpdated, cart } };
    }

    const lineItems = cart?.lines ?? [];

    // Early return if no line items
    if (lineItems.length === 0) {
      return { props: { cartLinesToBeUpdated, cart } };
    }

    const productLines = lineItems.filter(
      (line) => line.type === LineItemType.PRODUCT,
    );

    if (productLines.length === 0) {
      return { props: { cartLinesToBeUpdated, cart } };
    }

    const originalLines = cart.lines ?? [];

    // Revalidate against live offers: MsCart re-prices every offer-backed line,
    // clamps quantities to available stock, and removes lines whose offer is
    // gone.
    const revalidation = await cartApi.revalidateCart(cartId);

    if (revalidation) {
      cart = revalidation.cart ?? cart;

      revalidation.deltas.forEach((delta) => {
        const line =
          originalLines.find((l) => l.id === delta.id) ??
          cart?.lines?.find((l) => l.id === delta.id);

        if (!line) {
          return;
        }

        cartLinesToBeUpdated.push({
          line,
          stock: delta.newStock ?? 0,
          removed: delta.removed,
          priceChanged: delta.priceChanged,
          oldPrice: delta.oldPrice,
          newPrice: delta.newPrice,
          vendorChanged: delta.vendorChanged,
          oldVendor: delta.oldVendor,
          newVendor: delta.newVendor,
        });
      });
    }

    return {
      props: {
        cartLinesToBeUpdated,
        cart,
      },
    };
  } catch (err) {
    logger.error({ err }, 'Error fetching cart');

    return {
      props: {
        cartLinesToBeUpdated,
        cart,
      },
    };
  }
};

const CartPage = ({ cartLinesToBeUpdated, cart }: CartPageProps) => (
  <CartPageLayout
    cartLinesToBeUpdated={cartLinesToBeUpdated}
    initialCart={cart}
  />
);

export default CartPage;
